import { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContact = () => {
  const { t, contacts, refreshContacts } = useShop();
  const d = t.adminContact;
  const [form, setForm] = useState({ type: "phone", label_tr: "", label_en: "", value: "" });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ type: "phone", label_tr: "", label_en: "", value: "" });

  const apiError = (err) => {
    const detail = err.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : d.error);
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/contact`, form, { withCredentials: true });
      toast.success(d.added);
      setForm({ type: "phone", label_tr: "", label_en: "", value: "" });
      refreshContacts();
    } catch (err) {
      apiError(err);
    }
  };

  const save = async (id) => {
    try {
      await axios.patch(`${API}/admin/contact/${id}`, editForm, { withCredentials: true });
      toast.success(d.updated);
      setEditing(null);
      refreshContacts();
    } catch (err) {
      apiError(err);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/contact/${id}`, { withCredentials: true });
      toast.success(d.deleted);
      refreshContacts();
    } catch (err) {
      apiError(err);
    }
  };

  const labelCls = "text-[11px] tracking-[0.25em] uppercase text-brown-muted";
  const selectCls =
    "bg-transparent border border-brown/20 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-olive cursor-pointer";

  const Fields = ({ value, onChange, prefix }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <select
        value={value.type}
        onChange={(e) => onChange({ ...value, type: e.target.value })}
        className={selectCls}
        data-testid={`${prefix}-type`}
      >
        {Object.keys(d.types).map((k) => (
          <option key={k} value={k}>
            {d.types[k]}
          </option>
        ))}
      </select>
      <Input
        required
        value={value.label_tr}
        onChange={(e) => onChange({ ...value, label_tr: e.target.value })}
        placeholder={d.labelTr}
        className="editorial-input"
        data-testid={`${prefix}-label-tr`}
      />
      <Input
        required
        value={value.label_en}
        onChange={(e) => onChange({ ...value, label_en: e.target.value })}
        placeholder={d.labelEn}
        className="editorial-input"
        data-testid={`${prefix}-label-en`}
      />
      <Input
        required
        value={value.value}
        onChange={(e) => onChange({ ...value, value: e.target.value })}
        placeholder={d.valueHints[value.type]}
        className="editorial-input"
        data-testid={`${prefix}-value`}
      />
    </div>
  );

  return (
    <div data-testid="admin-contact">
      <h1 className="font-serif text-3xl sm:text-4xl font-medium text-brown">{d.tab}</h1>
      <p className="mt-4 text-base font-light text-brown-light max-w-xl">{d.sub}</p>

      <div className="mt-10 border border-brown/10 bg-cream-dark p-8">
        <div className="flex flex-col gap-4" data-testid="contact-list">
          {(contacts || []).length === 0 ? (
            <p className="text-sm text-brown-muted font-light">{d.empty}</p>
          ) : (
            (contacts || []).map((c) => (
              <div key={c.id} className="border border-brown/10 bg-cream p-5" data-testid={`contact-row-${c.id}`}>
                {editing === c.id ? (
                  <div className="flex flex-col gap-4">
                    <Fields value={editForm} onChange={setEditForm} prefix={`contact-edit-${c.id}`} />
                    <div className="flex gap-3">
                      <button
                        onClick={() => save(c.id)}
                        className="px-5 py-2 rounded-full bg-olive text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                        data-testid={`contact-save-${c.id}`}
                      >
                        {d.save}
                      </button>
                      <button
                        onClick={() => setEditing(null)}
                        className="px-5 py-2 rounded-full border border-brown/20 text-brown-light text-xs font-medium tracking-widest uppercase hover:text-brown transition-colors"
                        data-testid={`contact-cancel-${c.id}`}
                      >
                        {d.cancel}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="px-2.5 py-0.5 rounded-full bg-olive/15 text-olive-dark text-[10px] tracking-[0.15em] uppercase font-semibold">
                          {d.types[c.type] || c.type}
                        </span>
                        <p className="text-sm font-medium text-brown">{c.label_tr} · {c.label_en}</p>
                      </div>
                      <p className="mt-1.5 text-sm text-brown-light break-all">{c.value}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => {
                          setEditing(c.id);
                          setEditForm({ type: c.type, label_tr: c.label_tr, label_en: c.label_en, value: c.value });
                        }}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors"
                        data-testid={`contact-edit-${c.id}`}
                      >
                        <Pencil size={12} /> {d.edit}
                      </button>
                      <button
                        onClick={() => remove(c.id)}
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-800/30 text-red-800 text-xs font-medium tracking-widest uppercase hover:bg-red-800/5 transition-colors"
                        data-testid={`contact-delete-${c.id}`}
                      >
                        <Trash2 size={12} /> {d.delete}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        <form onSubmit={add} className="mt-8 border-t border-brown/10 pt-8 flex flex-col gap-4" data-testid="contact-add-form">
          <Fields value={form} onChange={setForm} prefix="contact-new" />
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brown text-cream text-xs font-medium tracking-widest uppercase hover:bg-olive-dark transition-colors w-fit"
            data-testid="contact-add"
          >
            <Plus size={14} /> {d.add}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminContact;
